import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { SendOutlined, UserAddOutlined } from '@ant-design/icons';
import { Switch, Tooltip, Input, Button, Spin as AntSpin, Drawer } from 'antd';

import CalculateCommissionAffiliate from 'components/CalculateCommissionAffiliate';
import MyCollapse from 'components/Collapse';
import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import CustomCopyToClipboard from 'components/CustomCopyToClipboard';
import ModalActions from 'components/ModalActions';
import Spin from 'components/Spin';
import { functions } from 'helpers';
import { HOST_ORBITA } from 'settings';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { useProductInviteAffiliate } from 'v2/hooks/useProductInviteAffiliate';
import { useProductStatusMutation } from 'v2/hooks/useProductStatusMutation';
import productService from 'v2/services/product';

import QuickViewLinks from './QuickviewLinks';

interface IProps {
  active: boolean;
  changeActive: any;
  onEditProduct?: any;
  productData: any;
}

const QuickView: React.FC<IProps> = ({ active, changeActive, onEditProduct, productData }) => {
  const history = useHistory();
  const statusMutation = useProductStatusMutation();
  const inviteAffiliateMutation = useProductInviteAffiliate();

  const [idProductHistory, setIdProductHistory] = useState<number>(null);
  const [modalStatusProduct, setModalStatusProduct] = useState(false);
  const [modalStatusProductArchive, setModalStatusProductArchive] = useState(false);
  const [modalCloneProduct, setModalCloneProduct] = useState(false);
  const [statusProduct, setStatusProduct] = useState<'active' | 'inactive' | 'filed' | ' '>(null);
  const [loading, setLoading] = useState(true);
  const [, setLoadingCloneProduct] = useState(false);
  const [inputInviteAffiliate, setInputInviteAffiliate] = useState<string>(null);
  const [, setInputInviteAffiliateError] = useState(false);
  const [inputDelivery, setInputDelivery] = useState<string>(null);
  const [, setInputDeliveryError] = useState(false);
  const [, setProductInvoiceData] = useState<any>(null);
  const [, setActionButtonDisabled] = useState(false);
  const [numberAffiliates, setNumberAffiliates] = useState(0);
  const [validationErrors] = useState<Record<string, string>>({});

  const productDataRef = useRef(productData);
  productDataRef.current = productData;

  const statusProductRef = useRef(statusProduct);
  statusProductRef.current = statusProduct;

  const prevActiveRef = useRef(active);

  const isValid = useCallback(
    (field: any) => {
      return !!validationErrors[field];
    },
    [validationErrors]
  );

  const errorFor = useCallback(
    (field: any) => {
      return validationErrors[field] || '';
    },
    [validationErrors]
  );

  const handleCloseQuickView = useCallback(() => {
    if (typeof changeActive === 'function') {
      changeActive();
    }

    setLoading(true);
    setIdProductHistory(productDataRef.current?.id);
  }, [changeActive]);

  const toggleModalStatusProduct = useCallback(() => {
    setModalStatusProduct(prev => !prev);
  }, []);

  const onCancelModal = useCallback(() => {
    toggleModalStatusProduct();
  }, [toggleModalStatusProduct]);

  const onConfirmModal = useCallback(async () => {
    toggleModalStatusProduct();
    setActionButtonDisabled(true);

    try {
      await statusMutation.mutateAsync({
        productId: productDataRef.current.id,
        status: statusProductRef.current === 'active' ? 0 : 1
      });

      setStatusProduct(statusProductRef.current === 'active' ? 'inactive' : 'active');
      setActionButtonDisabled(false);

      if (onEditProduct) {
        onEditProduct();
      }
    } catch {
      setActionButtonDisabled(false);
      toast.error('Ops, erro ao atualizar o status do produto');
    }
  }, [statusMutation, onEditProduct, toggleModalStatusProduct]);

  const toggleModalStatusProductArchive = useCallback(() => {
    setModalStatusProductArchive(prev => !prev);
  }, []);

  const onCancelModalArchive = useCallback(() => {
    toggleModalStatusProductArchive();
  }, [toggleModalStatusProductArchive]);

  const onConfirmModalArchive = useCallback(async () => {
    setActionButtonDisabled(true);
    toggleModalStatusProductArchive();

    try {
      await statusMutation.mutateAsync({
        productId: productDataRef.current.id,
        status: 2
      });

      setStatusProduct('filed');
      setActionButtonDisabled(false);

      if (onEditProduct) {
        onEditProduct();
      }
    } catch {
      setActionButtonDisabled(false);
      toast.error('Ops, não foi possível excluir o produto');
    }
  }, [statusMutation, onEditProduct, toggleModalStatusProductArchive]);

  const toggleModalCloneProduct = useCallback(() => {
    setModalCloneProduct(prev => !prev);
  }, []);

  const onCancelModalCloneProduct = useCallback(() => {
    toggleModalCloneProduct();
  }, [toggleModalCloneProduct]);

  const onConfirmModalCloneProduct = useCallback(async () => {
    toggleModalCloneProduct();
    setLoadingCloneProduct(true);
    setActionButtonDisabled(true);

    try {
      const result = await productService.cloneProduct(productDataRef.current.id);
      const clonedProduct = result?.product ?? result;
      history.push('/producer/product/editproduct/' + clonedProduct.id);
      functions.addScrollPage();
    } catch {
      toast.error('Ops, erro ao clonar o produto');
      setLoadingCloneProduct(false);
      setActionButtonDisabled(false);
    }
  }, [history, toggleModalCloneProduct]);

  const handleChange = useCallback(
    (prop: any) => (event: any) => {
      if (prop === 'inputInviteAffiliate') {
        setInputInviteAffiliateError(false);
        setInputInviteAffiliate(event.target.value);
      }

      if (prop === 'inputDelivery') {
        setInputDelivery(event.target.value);
      }
    },
    []
  );

  const handleClickInviteAffiliate = useCallback(async () => {
    setInputInviteAffiliateError(false);

    try {
      await inviteAffiliateMutation.mutateAsync({
        productId: productDataRef.current.id,
        email: inputInviteAffiliate
      });

      setInputInviteAffiliate('');
      setInputInviteAffiliateError(false);
      setNumberAffiliates(prev => prev + 1);

      if (onEditProduct) {
        onEditProduct();
      }

      toast.success('Sucesso, afiliado adicionado com êxito');
    } catch {
      toast.error('Ops, erro ao convidar afiliado');
    }
  }, [inviteAffiliateMutation, inputInviteAffiliate, onEditProduct]);

  const handleChangeStatusProduct = useCallback(() => {
    toggleModalStatusProduct();
  }, [toggleModalStatusProduct]);

  useEffect(() => {
    const wasInactive = prevActiveRef.current === false;
    prevActiveRef.current = active;

    if (!wasInactive || !active) {
      return;
    }

    if (productData.id && productData.id !== idProductHistory) {
      setLoading(true);
      setInputDeliveryError(false);
      setInputInviteAffiliateError(false);

      setLoading(false);
      setInputDelivery(idProductHistory === productData.id ? inputDelivery : '');
      setInputInviteAffiliate(idProductHistory === productData.id ? inputInviteAffiliate : '');
      setInputInviteAffiliateError(false);
      setStatusProduct(productData.status);
      setProductInvoiceData(null);
      setNumberAffiliates(productData.affiliates);
    } else {
      setLoading(false);
      setActionButtonDisabled(false);
    }
  }, [active, productData, idProductHistory, inputDelivery, inputInviteAffiliate]);

  if (!productData) {
    return (
      <Drawer
        onClose={handleCloseQuickView}
        placement='right'
        open={false}
        closable={false}
        styles={{ body: { padding: 0 }, wrapper: { maxWidth: '100%' } }}
        width={450}
      />
    );
  }

  const productDataReceive = productData;

  let labelToggleStatus = null;

  if (statusMutation.isPending) {
    labelToggleStatus = (
      <span style={{ position: 'relative', top: '3px' }}>
        <AntSpin size='small' />
      </span>
    );
  } else if (statusProduct === 'active') {
    labelToggleStatus = 'Ativo';
  } else if (statusProduct === 'inactive') {
    labelToggleStatus = 'Inativo';
  } else if (statusProduct === 'filed') {
    labelToggleStatus = 'Arquivado';
  } else {
    labelToggleStatus = 'Sem status';
  }

  return (
    <>
      <Drawer
        onClose={handleCloseQuickView}
        placement='right'
        open={active}
        styles={{ body: { padding: 0 }, wrapper: { maxWidth: '100%' } }}
        width={450}
        title={`Produto: ${productDataReceive.id}`}
      >
        {loading ? (
          <div className='loading h-full'>
            <Spin tip='Carregando produto ...' />
          </div>
        ) : (
          <>
            <div className='p-4'>
              <div className='flex items-start gap-3'>
                <div
                  className='bg-grey-100 h-20 w-20 shrink-0 rounded-lg bg-cover bg-center'
                  style={{ backgroundImage: productDataReceive.image ? `url(${productDataReceive.image})` : undefined }}
                />

                <div className='flex min-w-0 flex-1 flex-col gap-2'>
                  <span
                    className='text-text-heading truncate text-sm font-semibold'
                    title={productDataReceive.title || 'Produto desconhecido'}
                  >
                    {productDataReceive.title || 'Produto desconhecido'}
                  </span>

                  <label className='text-text-secondary flex items-center gap-2 text-xs'>
                    <Switch
                      size='small'
                      defaultChecked={statusProduct === 'active'}
                      checked={statusProduct === 'active'}
                      disabled={true}
                      onChange={handleChangeStatusProduct}
                    />
                    {labelToggleStatus}
                  </label>

                  {!productDataReceive.has_children && (
                    <div className='flex flex-col items-baseline'>
                      <span className='text-text-secondary text-[11px] tracking-wide uppercase'>Preço</span>
                      <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores'>
                        <span className='text-success text-lg leading-none font-bold'>
                          {productDataReceive.charge_type === 'A' && (
                            <FormattedNumber value={productDataReceive.recurring_value} format='BRL' />
                          )}
                          {productDataReceive.charge_type === 'N' && (
                            <FormattedNumber value={productDataReceive.price} format='BRL' />
                          )}
                          {productDataReceive.charge_type === 'G' && 'Grátis'}
                          {(!productDataReceive.charge_type ||
                            productDataReceive.charge_type === '' ||
                            productDataReceive.charge_type === ' ') && <FormattedNumber value={0} format='BRL' />}
                        </span>
                      </PermissionHide>
                      <PermissionHide inverse={false} action='myeduzz:meus-produtos:ocultar-valores'>
                        <em className='text-text-secondary text-sm not-italic'>oculto</em>
                      </PermissionHide>
                      {productDataReceive.charge_type === 'A' && (
                        <span className='text-text-secondary text-[11px]'>recorrente</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {!productDataReceive.has_children && (
                <div className='border-grey-100 mt-8 overflow-hidden rounded-lg border'>
                  <div className='divide-grey-100 grid grid-cols-3 divide-x'>
                    <div className='flex flex-col items-center gap-0.5 px-2 py-3'>
                      <span className='text-text-secondary text-[11px] tracking-wide uppercase'>Ganhos</span>
                      <span className='text-success text-sm font-semibold'>
                        <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores'>
                          <ColoredFormattedNumber
                            value={productDataReceive.total_gains}
                            format={productDataReceive.currency.code}
                          />
                        </PermissionHide>
                        <PermissionHide inverse={false} action='myeduzz:meus-produtos:ocultar-valores'>
                          <em className='text-text-secondary font-normal not-italic'>oculto</em>
                        </PermissionHide>
                      </span>
                    </div>

                    <div className='flex flex-col items-center gap-0.5 px-2 py-3'>
                      <span className='text-text-secondary text-[11px] tracking-wide uppercase'>Afiliados</span>
                      <span className='text-text-heading text-sm font-semibold'>{numberAffiliates}</span>
                    </div>

                    <div className='flex flex-col items-center gap-0.5 px-2 py-3'>
                      <span className='text-text-secondary text-[11px] tracking-wide uppercase'>Funil Infinito</span>
                      <span
                        className={`text-sm font-semibold ${productDataReceive.infinity_funnel ? 'text-success' : 'text-text-secondary'}`}
                      >
                        {productDataReceive.infinity_funnel ? 'Ativado' : 'Desativado'}
                      </span>
                    </div>
                  </div>

                  <div className='border-grey-100 divide-grey-100 grid grid-cols-3 divide-x border-t'>
                    <div className='flex flex-col items-center gap-0.5 px-2 py-3'>
                      <span className='text-text-secondary text-[11px] tracking-wide uppercase'>Comissão</span>
                      <span className='text-text-heading text-sm font-semibold'>
                        <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores'>
                          {productDataReceive.charge_type === 'A' && (
                            <CalculateCommissionAffiliate
                              price={productDataReceive.recurring_value}
                              commissionType={productDataReceive.recurring_comission_type}
                              commissionValue={productDataReceive.recurring_comission_value}
                            />
                          )}
                          {productDataReceive.charge_type === 'N' && (
                            <CalculateCommissionAffiliate
                              price={productDataReceive.price || 0}
                              commissionType={productDataReceive.comission_type}
                              commissionValue={productDataReceive.comission_value}
                            />
                          )}
                          {(!productDataReceive.charge_type ||
                            productDataReceive.charge_type === ' ' ||
                            productDataReceive.charge_type === '') && (
                            <CalculateCommissionAffiliate price={0} commissionValue={0} />
                          )}
                        </PermissionHide>
                        <PermissionHide inverse={false} action='myeduzz:meus-produtos:ocultar-valores'>
                          <em className='text-text-secondary font-normal not-italic'>oculto</em>
                        </PermissionHide>
                      </span>
                    </div>

                    <div className='flex flex-col items-center gap-0.5 px-2 py-3'>
                      <span className='text-text-secondary text-[11px] tracking-wide uppercase'>RPC/A</span>
                      <span className='text-text-heading text-sm font-semibold'>
                        {productDataReceive.rpc_affiliate || 0}
                      </span>
                    </div>

                    <div className='flex flex-col items-center gap-0.5 px-2 py-3'>
                      <span className='text-text-secondary text-[11px] tracking-wide uppercase'>RPC/P</span>
                      <span className='text-text-heading text-sm font-semibold'>
                        {productDataReceive.rpc_producer || 0}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className='mt-6' />
              {((!productDataReceive.has_children && statusProduct && statusProduct === 'active') ||
                statusProduct === 'inactive') && (
                <>
                  <h3 className='text-text-secondary mb-2! text-xs font-semibold tracking-wide uppercase'>
                    Ações Rápidas
                  </h3>
                  <QuickViewLinks product={productDataReceive} />
                </>
              )}
              <div className='mt-6' />
              {statusProduct === 'active' && (
                <>
                  <MyCollapse
                    id='invite-affiliate'
                    title='Adicionar Afiliados'
                    icon={<UserAddOutlined style={{ fontSize: 16 }} />}
                    arrow={true}
                    contentActive={true}
                  >
                    <CustomCopyToClipboard
                      text={`${HOST_ORBITA}/affiliate/invite/${productDataReceive.id}`}
                      disableUnderline={true}
                      style={{ width: '100%' }}
                    />
                    <div className='mt-4'></div>
                    <label htmlFor='invite-email' style={{ fontSize: 12 }}>
                      Convide por E-mail
                    </label>
                    <Input
                      id='invite-email'
                      type='text'
                      onChange={handleChange('inputInviteAffiliate')}
                      value={inputInviteAffiliate}
                      autoFocus={true}
                      status={isValid('inputInviteAffiliate') ? 'error' : undefined}
                      suffix={
                        <Button type='text' onClick={handleClickInviteAffiliate} size='small'>
                          {!inviteAffiliateMutation.isPending && (
                            <Tooltip title='Enviar'>
                              <SendOutlined style={{ fontSize: 16 }} />
                            </Tooltip>
                          )}
                          {inviteAffiliateMutation.isPending && <AntSpin size='small' />}
                        </Button>
                      }
                    />
                    {isValid('inputInviteAffiliate') && (
                      <small
                        style={{
                          fontSize: 12,
                          color: '#dc3f53',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        {errorFor('inputInviteAffiliate')}
                      </small>
                    )}
                  </MyCollapse>
                </>
              )}
            </div>
          </>
        )}
      </Drawer>

      <ModalActions
        open={modalStatusProduct}
        onClose={onCancelModal}
        onOk={onConfirmModal}
        title={`Deseja realmente ${statusProduct === 'active' ? 'inativar' : 'ativar'} este produto?`}
        buttonOk={statusProduct === 'active' ? 'Inativar' : 'Ativar'}
      />

      <ModalActions
        open={modalStatusProductArchive}
        onClose={onCancelModalArchive}
        onOk={onConfirmModalArchive}
        title={
          'Deseja realmente excluir este produto? Atenção, esta operação é irreversível e automaticamente todas variações serão arquivadas!'
        }
        buttonOk={'Excluir'}
      />

      <ModalActions
        open={modalCloneProduct}
        onClose={onCancelModalCloneProduct}
        onOk={onConfirmModalCloneProduct}
        title={'Deseja realmente duplicar este produto? Obs: você será redirecionado para a tela de edição.'}
        buttonOk={'Duplicar'}
      />
    </>
  );
};

export default QuickView;
